import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import Partners from "../../Components/Home/Partners/Partners";
import CTA from "../../Components/Home/CTA/CTA";

import { aboutData } from "../../Data/aboutData";
import { fadeLeft, fadeRight, fadeUp, viewportOnce } from "../../Utils/animations";
import { DataLakeVisual, PipelineVisual, QualityRingVisual } from "./AboutVisuals";

import "../../Style/about/about.css";

function About() {
  return (
    <div className="about-page">
      <Seo
        title="About Us | Conectify Tech"
        description="Learn how Conectify Tech delivers verified B2B contact intelligence, role-specified data, and real-time CRM pipelines."
      />

      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {/* Atmospheric Ambient Glow Spheres */}
      <div className="about-ambient-blob about-ambient-blob--blue" />
      <div className="about-ambient-blob about-ambient-blob--cyan" />
      <div className="about-ambient-blob about-ambient-blob--purple" />

      <main id="main">
        {/* ==================================================================
            1. Let's Have Intro... Section
            ================================================================== */}
        <section className="about-section">
          <Container className="about-grid-2">
            <motion.div
              variants={fadeLeft()}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <span className="section-eyebrow eye">
                <span className="section-eyebrow__dot" aria-hidden="true" />
                {aboutData.intro.eyebrow}
              </span>

              <h1 className="section-title mt-4">
                {aboutData.intro.title}{" "}
                <span className="text-gradient">{aboutData.intro.highlight}</span>
              </h1>

              <div className="mt-5 space-y-4">
                {aboutData.intro.paragraphs.map((p, idx) => (
                  <p key={idx} className="about-desc">
                    {p}
                  </p>
                ))}
              </div>
            </motion.div>

            {/* Live Data Stream Graphic Visual */}
            <motion.div
              variants={fadeRight(0.12)}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <DataLakeVisual />
            </motion.div>
          </Container>
        </section>

        {/* ==================================================================
            2. We Help You To Get High Quality B2B Leads (5 Steps)
            ================================================================== */}
        <section className="about-section about-section--tint">
          <Container className="about-grid-2">
            <motion.div
              variants={fadeLeft()}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <span className="section-eyebrow eye">Proven Pipeline</span>
              <h2 className="section-title mt-4">{aboutData.leadGen.title}</h2>
              <p className="about-desc mt-3">{aboutData.leadGen.description}</p>

              <div className="steps-stack">
                {aboutData.leadGen.steps.map((step, idx) => (
                  <motion.div
                    key={idx}
                    className="step-card"
                    variants={fadeUp(idx * 0.08)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                  >
                    <span className="step-card__num">{`0${idx + 1}`}</span>
                    <span className="step-card__text">{step}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Interactive Pipeline Ingestion Graphic */}
            <motion.div
              variants={fadeRight(0.15)}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <PipelineVisual />
            </motion.div>
          </Container>
        </section>

        {/* ==================================================================
            3. Structured Around Your Criteria (3 Tier Cards)
            ================================================================== */}
        <section className="about-section">
          <Container>
            <div className="text-center max-w-xl mx-auto mb-4">
              <span className="section-eyebrow eye">Database Depth</span>
              <h2 className="section-title mt-4">
                Structured Around <span className="text-gradient">Your Criteria</span>
              </h2>
            </div>

            <div className="data-tier-grid">
              {aboutData.segmentedData.map((col, idx) => {
                const Icon = col.icon;
                return (
                  <motion.div
                    key={col.id}
                    className="data-tier-card"
                    variants={fadeUp(idx * 0.12)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                  >
                    <div className="data-tier-card__icon-box">
                      <Icon size={24} />
                    </div>
                    <h3 className="data-tier-card__title">{col.title}</h3>
                    <ul className="data-tier-card__list">
                      {col.points.map((pt, pIdx) => (
                        <li key={pIdx} className="data-tier-card__item">
                          <CheckCircle2 size={16} />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </Container>
        </section>

        {/* ==================================================================
            4. Real Time Data Highlight Banner
            ================================================================== */}
        <section className="action-strip">
          <Container>
            <motion.div
              className="action-strip__box"
              variants={fadeUp()}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <h3 className="action-strip__title">{aboutData.ctaBanner.title}</h3>
              <Button
                size="md"
                icon={<ArrowRight size={16} />}
                onClick={() => (window.location.href = aboutData.ctaBanner.buttonLink)}
              >
                {aboutData.ctaBanner.buttonText}
              </Button>
            </motion.div>
          </Container>
        </section>

        {/* ==================================================================
            5. Our Skill & ROI Progress Gauges
            ================================================================== */}
        <section className="about-section about-section--tint">
          <Container className="about-grid-2">
            <motion.div
              variants={fadeLeft()}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <span className="section-eyebrow eye">{aboutData.skills.eyebrow}</span>
              <h2 className="section-title mt-4">{aboutData.skills.title}</h2>
              <p className="about-desc mt-4">{aboutData.skills.description}</p>

              <div className="skill-trackers">
                {aboutData.skills.bars.map((bar) => (
                  <div key={bar.label} className="skill-row">
                    <div className="skill-row__meta">
                      <span>{bar.label}</span>
                      <span>{bar.percentage}%</span>
                    </div>
                    <div className="skill-row__bar">
                      <motion.div
                        className="skill-row__fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: `${bar.percentage}%` }}
                        viewport={viewportOnce}
                        transition={{ duration: 1.1, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Radial Percentage Visual */}
            <motion.div
              variants={fadeRight(0.12)}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <QualityRingVisual />
            </motion.div>
          </Container>
        </section>

        {/* Global Reusable Enterprise Sections */}
        <Partners />
        <CTA />
      </main>
    </div>
  );
}

export default About;